"use client";
import {
  AlertTriangle,
  Archive,
  Binary,
  Box,
  Check,
  ChevronDown,
  ChevronRight,
  CircleStop,
  Copy,
  Download,
  File,
  FileArchive,
  FileCode2,
  FileJson,
  FileText,
  Folder,
  Info,
  LoaderCircle,
  LockKeyhole,
  Search,
  Sparkles,
  TextQuote,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  inlineRecoveredStrings,
} from "../../../lib/class-rewriter";
import {
  extractContainerEntry,
  inspectContainer,
  recoverZipPassword,
  type ContainerEntryInfo,
  type ContainerInspection,
  type PasswordRecoveryProgress,
  type PasswordRecoveryResult,
} from "../../../lib/container-inspector";
import {
  applyRecoveryRecipe,
  archiveStringKeys,
  discoverRecoveryCandidates,
  parseManualKeyList,
  type RecoveryCandidate,
  type RecoveryResult,
} from "../../../lib/deobfuscation";
import {
  byteEntropy,
  detectFileFormat,
  extractPrintableStrings,
  suggestedRecoveredName,
  type DetectedFileFormat,
} from "../../../lib/file-formats";
import {
  highlightJava,
} from "../../../lib/java-highlighter";
import {
  decompileClassResilient,
} from "../../../lib/source-recovery";
import {
  type ArchiveEntry,
  type ClassInfo,
  type ScanReport,
  type ScanSession,
} from "../../../lib/types";
import {
  formatBytes,
} from "../../shared/format";
import {
  downloadBlob,
} from "../../shared/io";
import {
  StatusDot,
} from "../../shared/primitives";


export type EntryMode =
  | "preview"
  | "raw-source"
  | "vineflower"
  | "bytecode"
  | "hex"
  | "strings"
  | "unpack"
  | "deobfuscate"
  | "metadata";


interface TreeNode {
  name: string;
  path: string;
  directory: boolean;
  entry?: ArchiveEntry;
  children: Map<string, TreeNode>;
}


export function buildTree(entries: ArchiveEntry[]) {
  const root: TreeNode = {
    name: "",
    path: "",
    directory: true,
    children: new Map(),
  };

  for (const entry of entries.filter((item) => !item.directory)) {
    const parts = entry.path.split("/").filter(Boolean);
    let cursor = root;
    parts.forEach((part, index) => {
      const path = parts.slice(0, index + 1).join("/");
      const directory = index < parts.length - 1;
      if (!cursor.children.has(part)) {
        cursor.children.set(part, {
          name: part,
          path,
          directory,
          entry: directory ? undefined : entry,
          children: new Map(),
        });
      }
      cursor = cursor.children.get(part)!;
    });
  }
  return root;
}


export function treeChildren(node: TreeNode) {
  return Array.from(node.children.values()).sort(
    (left, right) =>
      Number(right.directory) - Number(left.directory) ||
      left.name.localeCompare(right.name),
  );
}


export function hexDump(bytes: Uint8Array, limit = 8192) {
  const lines: string[] = [];
  const length = Math.min(bytes.length, limit);
  for (let offset = 0; offset < length; offset += 16) {
    const chunk = bytes.slice(offset, Math.min(offset + 16, length));
    const address = offset.toString(16).padStart(8, "0");
    const hex = Array.from(chunk)
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join(" ")
      .padEnd(47, " ");
    const ascii = Array.from(chunk)
      .map((byte) => (byte >= 32 && byte <= 126 ? String.fromCharCode(byte) : "."))
      .join("");
    lines.push(`${address}  ${hex}  |${ascii}|`);
  }
  if (bytes.length > limit) {
    lines.push(
      `\n… ${formatBytes(bytes.length - limit)} omitted from the preview`,
    );
  }
  return lines.join("\n");
}


export function annotatedJavaSource(
  source: string,
  recovered: ScanReport["recoveredExpressions"],
  flows: ScanReport["dataFlows"],
) {
  if (!recovered.length && !flows.length) return source;
  const javaLiteral = (value: string) =>
    JSON.stringify(value)
      .replaceAll("\u2028", "\\u2028")
      .replaceAll("\u2029", "\\u2029");
  const valuesByOriginal = new Map<string, Set<string>>();
  for (const item of recovered) {
    if (
      item.confidence !== "high" ||
      item.expression.length < 2 ||
      item.expression.length > 600 ||
      item.value.length > 16_384
    ) {
      continue;
    }
    const values = valuesByOriginal.get(item.expression) ?? new Set<string>();
    values.add(item.value);
    valuesByOriginal.set(item.expression, values);
  }
  let reconstructedSource = source;
  let inlineCount = 0;
  for (const [original, values] of valuesByOriginal) {
    if (values.size !== 1 || inlineCount >= 250) continue;
    const encryptedLiteral = javaLiteral(original);
    if (!reconstructedSource.includes(encryptedLiteral)) continue;
    const decodedLiteral = javaLiteral([...values][0]);
    reconstructedSource = reconstructedSource.replaceAll(
      encryptedLiteral,
      `${decodedLiteral} /* decoded by JarLab */`,
    );
    inlineCount += 1;
  }
  const clean = (value: string) =>
    value.replace(/\s+/g, " ").replaceAll("*/", "* /").slice(0, 220);
  const analysis = [
    "/* JarLab static string analysis",
    ` * ${recovered.filter((item) => item.confidence === "high").length.toLocaleString()} exact value${recovered.filter((item) => item.confidence === "high").length === 1 ? "" : "s"} recovered; ${recovered.filter((item) => item.confidence === "high" && item.patchable).length.toLocaleString()} call site${recovered.filter((item) => item.confidence === "high" && item.patchable).length === 1 ? "" : "s"} proven rewrite-safe.`,
    inlineCount
      ? ` * ${inlineCount.toLocaleString()} matching source literal${inlineCount === 1 ? "" : "s"} annotated in this view.`
      : "",
    ...recovered
      .slice(0, 24)
      .map(
        (item) =>
          ` * [${item.method} @ ${item.offset}] ${item.transform}: ${clean(item.value)}`,
      ),
    ...flows
      .slice(0, 12)
      .map(
        (flow) =>
          ` * [${flow.severity.toUpperCase()}] ${flow.sourceLabel} -> ${flow.sinkLabel}${flow.reachable ? " (entry-point reachable)" : ""}`,
      ),
    recovered.length > 24 || flows.length > 12
      ? ` * ${Math.max(0, recovered.length - 24) + Math.max(0, flows.length - 12)} additional analysis records are available in the workspace.`
      : "",
    " */",
    "",
  ]
    .filter(Boolean)
    .join("\n");
  return `${analysis}${reconstructedSource}`;
}


function nestedClassNames(report: ScanReport, internalName: string) {
  const prefix = `${internalName}$`;
  return report.classes
    .map((item) => item.name.replaceAll(".", "/"))
    .filter((name) => name.startsWith(prefix));
}


export async function decompileClassWithRecoveredConstants(
  report: ScanReport,
  session: ScanSession,
  classInfo: ClassInfo,
  engine: "cfr" | "vineflower" = "cfr",
) {
  const internalName = classInfo.name.replaceAll(".", "/");
  const nestedClasses = nestedClassNames(report, internalName);
  const original = await decompileClassResilient(internalName, classInfo, {
    source: session.getClassBytes,
    engine,
    nestedClasses,
  });
  const recovered = report.recoveredExpressions.filter(
    (item) =>
      (item.entry === classInfo.path || item.className === classInfo.name) &&
      item.patchable &&
      item.confidence === "high",
  );
  if (!recovered.length) return original;
  const originalBytes = await session.getClassBytes(internalName);
  if (!originalBytes) return original;
  try {
    const rewritten = inlineRecoveredStrings(
      originalBytes,
      classInfo,
      recovered,
    );
    if (!rewritten.applied) return original;
    const rewrittenSource = await decompileClassResilient(
      internalName,
      classInfo,
      {
        source: async (requestedName) => {
          const normalized = requestedName
            .replace(/\.class$/i, "")
            .replaceAll(".", "/");
          return normalized === internalName
            ? rewritten.bytes
            : session.getClassBytes(requestedName);
        },
        engine,
        nestedClasses,
      },
    );
    return {
      ...rewrittenSource,
      raw: original.raw,
      changes: [
        `Rewrote ${rewritten.applied.toLocaleString()} proven constant string call site${rewritten.applied === 1 ? "" : "s"} in bytecode before decompilation`,
        ...rewrittenSource.changes,
      ],
    };
  } catch {
    return original;
  }
}


export function JavaSourceEditor({
  source,
  classInfo,
  decompiled,
  recovered,
  flows,
  sourceLabel,
}: {
  source: string;
  classInfo: ClassInfo;
  decompiled: boolean;
  recovered: ScanReport["recoveredExpressions"];
  flows: ScanReport["dataFlows"];
  sourceLabel?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  const [viewport, setViewport] = useState({ scrollTop: 0, height: 720 });
  const annotatedSource = useMemo(
    () => annotatedJavaSource(source, recovered, flows),
    [flows, recovered, source],
  );
  const sourceLines = useMemo(() => annotatedSource.split("\n"), [annotatedSource]);
  const hasOversizedLines = useMemo(
    () => sourceLines.some((line) => line.length > 24_000),
    [sourceLines],
  );
  const virtualized =
    sourceLines.length > 1_200 || annotatedSource.length > 800_000;
  const lineHeight = 20;
  const overscan = 80;
  const startLine = virtualized
    ? Math.max(0, Math.floor(viewport.scrollTop / lineHeight) - overscan)
    : 0;
  const endLine = virtualized
    ? Math.min(
        sourceLines.length,
        Math.ceil((viewport.scrollTop + viewport.height) / lineHeight) +
          overscan,
      )
    : sourceLines.length;
  const visibleSource = useMemo(
    () =>
      sourceLines
        .slice(startLine, endLine)
        .map((line) =>
          line.length > 24_000
            ? `${line.slice(0, 24_000)} /* … viewer clipped this oversized line; Copy retains the complete source … */`
            : line,
        )
        .join("\n"),
    [endLine, sourceLines, startLine],
  );
  const lines = useMemo(
    () =>
      highlightJava(visibleSource).map((line, index) => ({
        ...line,
        number: startLine + index + 1,
      })),
    [startLine, visibleSource],
  );
  const fileName = `${classInfo.simpleName}.java`;
  const breadcrumbs = classInfo.name.split(".");

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    element.scrollTop = 0;
    setViewport({ scrollTop: 0, height: element.clientHeight || 720 });
  }, [annotatedSource]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      setViewport((current) => ({
        ...current,
        height: element.clientHeight || current.height,
      }));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const handleSourceScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!virtualized || frameRef.current !== null) return;
      const element = event.currentTarget;
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        setViewport({
          scrollTop: element.scrollTop,
          height: element.clientHeight || 720,
        });
      });
    },
    [virtualized],
  );

  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  return (
    <div className="ide-editor">
      <div className="ide-tab-strip">
        <div className="ide-tab is-active">
          <FileCode2 size={13} />
          <span>{fileName}</span>
          <span className="ide-tab-state" title="Read-only">
            R
          </span>
        </div>
        <div className="ide-editor-meta">
          <span>
            {sourceLabel ?? (decompiled ? "CFR source" : "Structural preview")}
          </span>
          {(recovered.length > 0 || flows.length > 0) && (
            <span>
              {recovered.filter((item) => item.confidence === "high").length} recovered ·{" "}
              {
                recovered.filter(
                  (item) => item.confidence === "high" && item.patchable,
                ).length
              }{" "}
              rewrite-ready · {flows.length} flows
            </span>
          )}
          <span>UTF-8</span>
          <span>LF</span>
        </div>
      </div>
      <div className="ide-breadcrumbs" aria-label="Class path">
        {breadcrumbs.map((part, index) => (
          <span key={`${part}-${index}`}>
            {index > 0 && <ChevronRight size={10} />}
            {index === breadcrumbs.length - 1 ? (
              <FileCode2 size={11} />
            ) : (
              <Box size={11} />
            )}
            {part}
          </span>
        ))}
      </div>
      <div
        ref={scrollRef}
        className="ide-code-scroll"
        role="region"
        aria-label={`Read-only Java source for ${classInfo.name}`}
        tabIndex={0}
        onScroll={handleSourceScroll}
      >
        <pre>
          <code>
            {virtualized && startLine > 0 && (
              <span
                className="ide-code-spacer"
                style={{ height: startLine * lineHeight }}
                aria-hidden="true"
              />
            )}
            {lines.map((line) => (
              <span className="ide-code-line" key={line.number}>
                <span className="ide-line-number" aria-hidden="true">
                  {line.number}
                </span>
                <span className="ide-line-source">
                  {line.tokens.length
                    ? line.tokens.map((token, index) => (
                        <span
                          className={`java-token token-${token.kind}`}
                          key={`${line.number}-${index}`}
                        >
                          {token.text}
                        </span>
                      ))
                    : "\u200B"}
                </span>
              </span>
            ))}
            {virtualized && endLine < sourceLines.length && (
              <span
                className="ide-code-spacer"
                style={{ height: (sourceLines.length - endLine) * lineHeight }}
                aria-hidden="true"
              />
            )}
          </code>
        </pre>
      </div>
      <div className="ide-editor-status">
        <span>Java</span>
        <span>{sourceLines.length.toLocaleString()} lines</span>
        {(virtualized || hasOversizedLines) && <span>Bounded rendering</span>}
        <span>Spaces: 4</span>
        <span>Read-only</span>
      </div>
    </div>
  );
}


export function EntryGlyph({
  kind,
  directory = false,
  size = 14,
}: {
  kind: ArchiveEntry["kind"];
  directory?: boolean;
  size?: number;
}) {
  if (directory) return <Folder size={size} />;
  if (kind === "class") return <FileCode2 size={size} />;
  if (kind === "manifest" || kind === "text" || kind === "document")
    return <FileText size={size} />;
  if (kind === "config" || kind === "database") return <FileJson size={size} />;
  if (kind === "archive") return <FileArchive size={size} />;
  if (kind === "script" || kind === "bytecode") return <FileCode2 size={size} />;
  if (
    kind === "native" ||
    kind === "binary" ||
    kind === "media" ||
    kind === "font"
  )
    return <Binary size={size} />;
  return <File size={size} />;
}


export function TreeRow({
  node,
  depth,
  expanded,
  setExpanded,
  selectedPath,
  onSelect,
}: {
  node: TreeNode;
  depth: number;
  expanded: Set<string>;
  setExpanded: (next: Set<string>) => void;
  selectedPath: string | null;
  onSelect: (entry: ArchiveEntry) => void;
}) {
  const isOpen = expanded.has(node.path);
  const toggle = () => {
    const next = new Set(expanded);
    if (isOpen) next.delete(node.path);
    else next.add(node.path);
    setExpanded(next);
  };
  return (
    <>
      <button
        type="button"
        className={`tree-row ${selectedPath === node.path ? "is-selected" : ""} ${
          node.entry?.suspicious ? "is-suspicious" : ""
        }`}
        style={{ paddingLeft: `${10 + depth * 16}px` }}
        onClick={() => (node.directory ? toggle() : node.entry && onSelect(node.entry))}
        title={node.path}
      >
        <span className="tree-chevron">
          {node.directory ? (
            isOpen ? (
              <ChevronDown size={12} />
            ) : (
              <ChevronRight size={12} />
            )
          ) : null}
        </span>
        <EntryGlyph
          kind={node.entry?.kind ?? "binary"}
          directory={node.directory}
        />
        <span>{node.name}</span>
        {node.entry?.suspicious && <StatusDot severity="high" />}
      </button>
      {node.directory &&
        isOpen &&
        treeChildren(node).map((child) => (
          <TreeRow
            key={child.path}
            node={child}
            depth={depth + 1}
            expanded={expanded}
            setExpanded={setExpanded}
            selectedPath={selectedPath}
            onSelect={onSelect}
          />
        ))}
    </>
  );
}


export function BytecodeView({
  classInfo,
  onCopy,
}: {
  classInfo: ClassInfo;
  onCopy: (value: string, message?: string) => void;
}) {
  const initialMethod = Math.max(
    0,
    classInfo.methods.findIndex((method) => method.instructions.length > 0),
  );
  const [methodIndex, setMethodIndex] = useState(initialMethod);
  const method = classInfo.methods[methodIndex] ?? classInfo.methods[0];
  const instructionCount = classInfo.methods.reduce(
    (sum, item) => sum + item.instructions.length,
    0,
  );

  const copyMethod = () => {
    if (!method) return;
    const listing = method.instructions
      .map(
        (instruction) =>
          `${instruction.offset.toString().padStart(5, "0")}  ${instruction.mnemonic.padEnd(
            19,
          )}${instruction.operands.padEnd(18)}${
            instruction.reference ? ` ; ${instruction.reference}` : ""
          }`,
      )
      .join("\n");
    onCopy(listing, "Bytecode listing copied");
  };

  return (
    <div className="bytecode-workbench">
      <aside className="bytecode-methods">
        <div className="bytecode-methods-heading">
          <span>METHODS</span>
          <small>{classInfo.methods.length}</small>
        </div>
        <div className="bytecode-method-list">
          {classInfo.methods.map((item, index) => (
            <button
              className={methodIndex === index ? "is-active" : ""}
              key={`${item.name}-${item.descriptor}-${index}`}
              type="button"
              onClick={() => setMethodIndex(index)}
            >
              <span>
                <strong>{item.name}</strong>
                <small>{item.descriptor}</small>
              </span>
              <em>{item.instructions.length}</em>
            </button>
          ))}
        </div>
        <div className="bytecode-budget">
          <Binary size={13} />
          <span>
            <strong>{instructionCount.toLocaleString()}</strong>
            <small>decoded instructions</small>
          </span>
        </div>
      </aside>
      <section className="bytecode-listing">
        {method ? (
          <>
            <div className="bytecode-listing-head">
              <div>
                <span>
                  {method.access.join(" ") || "package"} {method.name}
                </span>
                <code>{method.descriptor}</code>
              </div>
              <span>
                <small>{method.codeSize.toLocaleString()} bytes</small>
                <button
                  type="button"
                  onClick={copyMethod}
                  disabled={!method.instructions.length}
                >
                  <Copy size={12} />
                  Copy listing
                </button>
              </span>
            </div>
            {method.instructions.length ? (
              <div className="instruction-table" role="table">
                <div className="instruction-head" role="row">
                  <span>OFFSET</span>
                  <span>OP</span>
                  <span>INSTRUCTION</span>
                  <span>OPERANDS / REFERENCE</span>
                </div>
                {method.instructions.map((instruction) => (
                  <div
                    className="instruction-row"
                    role="row"
                    key={`${methodIndex}-${instruction.offset}`}
                  >
                    <code>{instruction.offset.toString().padStart(4, "0")}</code>
                    <code>
                      {instruction.opcode.toString(16).padStart(2, "0")}
                    </code>
                    <strong>{instruction.mnemonic}</strong>
                    <span>
                      {instruction.operands && <code>{instruction.operands}</code>}
                      {instruction.reference && (
                        <small title={instruction.reference}>
                          {instruction.reference}
                        </small>
                      )}
                    </span>
                  </div>
                ))}
                {method.bytecodeTruncated && (
                  <div className="bytecode-truncated">
                    <AlertTriangle size={13} />
                    Listing capped to keep this browser workspace responsive.
                  </div>
                )}
              </div>
            ) : (
              <div className="bytecode-empty">
                <Binary size={28} />
                <strong>No Code attribute</strong>
                <span>
                  This method is abstract, native, malformed, or outside the
                  workspace instruction budget.
                </span>
              </div>
            )}
          </>
        ) : (
          <div className="bytecode-empty">
            <Binary size={28} />
            <strong>No methods in this class</strong>
          </div>
        )}
      </section>
    </div>
  );
}


export function FormatOverview({
  format,
  bytes,
  stringCount,
}: {
  format: DetectedFileFormat;
  bytes: Uint8Array;
  stringCount: number;
}) {
  return (
    <div className="format-overview">
      <div className="format-mark">
        <EntryGlyph kind={format.category} size={26} />
      </div>
      <div className="format-identity">
        <span>DETECTED FORMAT</span>
        <strong>{format.label}</strong>
        <p>
          {format.details.join(" · ") ||
            "No stronger magic signature was recovered from the available bytes."}
        </p>
      </div>
      <div className="format-metrics">
        <span>
          <small>Detection</small>
          <strong>{format.confidence}</strong>
        </span>
        <span>
          <small>Entropy</small>
          <strong>{byteEntropy(bytes.subarray(0, 8 * 1024 * 1024)).toFixed(2)}</strong>
        </span>
        <span>
          <small>Strings</small>
          <strong>{stringCount.toLocaleString()}</strong>
        </span>
      </div>
    </div>
  );
}


export function SafeImagePreview({
  bytes,
  format,
  name,
}: {
  bytes: Uint8Array;
  format: DetectedFileFormat;
  name: string;
}) {
  const source = useMemo(() => {
    if (
      format.preview !== "image" ||
      format.mime === "image/svg+xml" ||
      !bytes.length
    ) {
      return "";
    }
    return URL.createObjectURL(
      new Blob([new Uint8Array(bytes)], { type: format.mime }),
    );
  }, [bytes, format.mime, format.preview]);
  useEffect(
    () => () => {
      if (source) URL.revokeObjectURL(source);
    },
    [source],
  );
  if (!source) return null;
  return (
    <div className="safe-image-preview">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={source} alt={`Static preview of ${name}`} />
      <span>Rendered as an inert image resource. No scripts are executed.</span>
    </div>
  );
}


export function ExtractedStringsView({
  strings,
  onCopy,
}: {
  strings: ReturnType<typeof extractPrintableStrings>;
  onCopy: (value: string, message?: string) => void;
}) {
  const [query, setQuery] = useState("");
  const visible = strings
    .filter((item) => item.value.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 2_000);
  return (
    <div className="entry-strings-workbench">
      <div className="entry-tool-header">
        <div>
          <span>PRINTABLE STRING CARVING</span>
          <strong>{strings.length.toLocaleString()} values</strong>
        </div>
        <div className="search-field compact">
          <Search size={14} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Filter strings"
            aria-label="Filter extracted strings"
          />
        </div>
      </div>
      <div className="entry-string-list">
        {visible.map((item, index) => (
          <button
            type="button"
            key={`${item.offset}-${item.encoding}-${index}`}
            onClick={() => onCopy(item.value, "String copied")}
          >
            <code>0x{item.offset.toString(16).padStart(8, "0")}</code>
            <span>{item.encoding}</span>
            <strong>{item.value}</strong>
            <Copy size={12} />
          </button>
        ))}
        {!visible.length && (
          <div className="entry-tool-empty">
            <TextQuote size={24} />
            <strong>No matching printable strings</strong>
          </div>
        )}
      </div>
    </div>
  );
}


export function ContainerWorkbench({
  bytes,
  path,
  report,
  onToast,
}: {
  bytes: Uint8Array;
  path: string;
  report: ScanReport;
  onToast: (message: string) => void;
}) {
  const [inspection, setInspection] = useState<ContainerInspection | null>(null);
  const [inspectionError, setInspectionError] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ContainerEntryInfo | null>(null);
  const [extracted, setExtracted] = useState<Uint8Array | null>(null);
  const [extractError, setExtractError] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordList, setPasswordList] = useState("");
  const [passwordProgress, setPasswordProgress] =
    useState<PasswordRecoveryProgress | null>(null);
  const [passwordResult, setPasswordResult] =
    useState<PasswordRecoveryResult | null>(null);
  const [recoveringPassword, setRecoveringPassword] = useState(false);
  const [query, setQuery] = useState("");
  const passwordAbort = useRef<AbortController | null>(null);

  useEffect(() => {
    let active = true;
    void inspectContainer(bytes, path)
      .then((result) => {
        if (!active) return;
        setInspection(result);
      })
      .catch((error) => {
        if (!active) return;
        setInspectionError(
          error instanceof Error
            ? error.message
            : "The container could not be inspected safely.",
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      passwordAbort.current?.abort();
    };
  }, [bytes, path]);

  const contextualPasswords = useMemo(
    () =>
      report.strings
        .map((item) => item.value.trim())
        .filter(
          (value) =>
            value.length >= 3 &&
            value.length <= 64 &&
            /^[\x20-\x7e]+$/.test(value) &&
            /(?:password|passwd|pwd|secret|key|infected|malware|minecraft|client)/i.test(
              value,
            ),
        )
        .slice(0, 160),
    [report.strings],
  );

  const runPasswordRecovery = async () => {
    setRecoveringPassword(true);
    setPasswordResult(null);
    setPasswordProgress(null);
    setExtractError("");
    const controller = new AbortController();
    passwordAbort.current = controller;
    try {
      const result = await recoverZipPassword(
        bytes,
        path,
        [password, ...passwordList.split(/\r?\n/)],
        contextualPasswords,
        setPasswordProgress,
        controller.signal,
      );
      setPasswordResult(result);
      if (result.password) {
        setPassword(result.password);
        onToast("Archive password recovered");
      }
    } catch (error) {
      setExtractError(
        error instanceof Error
          ? error.message
          : "Password recovery could not inspect this container.",
      );
    } finally {
      passwordAbort.current = null;
      setRecoveringPassword(false);
    }
  };

  const openEntry = async (entry: ContainerEntryInfo) => {
    if (entry.directory) return;
    setSelected(entry);
    setExtracted(null);
    setExtractError("");
    setExtracting(true);
    try {
      const value = await extractContainerEntry(
        bytes,
        path,
        entry.path,
        password || undefined,
      );
      setExtracted(value);
    } catch (error) {
      setExtractError(
        error instanceof Error
          ? error.message
          : "The selected entry could not be extracted.",
      );
    } finally {
      setExtracting(false);
    }
  };

  const downloadExtracted = () => {
    if (!selected || !extracted) return;
    const format = detectFileFormat(extracted, selected.path);
    downloadBlob(
      new Uint8Array(extracted),
      selected.path.split("/").pop() || "unpacked.bin",
      format.mime,
    );
    onToast("Unpacked entry downloaded");
  };

  if (loading) {
    return (
      <div className="entry-loading">
        <LoaderCircle size={20} />
        Inspecting container structure…
      </div>
    );
  }
  if (inspectionError) {
    return (
      <div className="entry-tool-error">
        <AlertTriangle size={18} />
        <div>
          <strong>Container inspection failed</strong>
          <span>{inspectionError}</span>
        </div>
      </div>
    );
  }
  if (!inspection) return null;

  const entries = inspection.entries.filter((entry) =>
    entry.path.toLowerCase().includes(query.toLowerCase()),
  );
  const extractedFormat =
    selected && extracted ? detectFileFormat(extracted, selected.path) : null;
  const extractedStrings = extracted
    ? extractPrintableStrings(extracted, { limit: 120, maxBytes: 1024 * 1024 })
    : [];

  return (
    <div className="container-workbench">
      <div className="entry-tool-header container-header">
        <div>
          <span>SAFE CONTAINER INSPECTION</span>
          <strong>{inspection.format.label}</strong>
          <small>
            {inspection.entries.length.toLocaleString()} entries ·{" "}
            {formatBytes(inspection.totalExpandedBytes)} expanded
          </small>
        </div>
        <div className="search-field compact">
          <Search size={14} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Filter contained files"
            aria-label="Filter contained files"
          />
        </div>
      </div>

      {inspection.encryptedEntries > 0 && (
        <section className="password-lab">
          <div className="password-lab-title">
            <LockKeyhole size={18} />
            <div>
              <strong>
                {inspection.encryptedEntries} encrypted{" "}
                {inspection.encryptedEntries === 1 ? "entry" : "entries"}
              </strong>
              <span>
                Bounded to 512 local attempts. Manual candidates run first.
              </span>
            </div>
          </div>
          <div className="password-controls">
            <label>
              <span>Current password</span>
              <input
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Enter a known password"
                autoComplete="off"
              />
            </label>
            <label>
              <span>Candidate dictionary, one per line</span>
              <textarea
                value={passwordList}
                onChange={(event) => setPasswordList(event.target.value)}
                placeholder={"infected\nmalware\ncustom-password"}
                rows={3}
              />
            </label>
            <button
              type="button"
              className="primary-button compact"
              onClick={() =>
                recoveringPassword
                  ? passwordAbort.current?.abort()
                  : void runPasswordRecovery()
              }
            >
              {recoveringPassword ? (
                <CircleStop size={14} />
              ) : (
                <LockKeyhole size={14} />
              )}
              {recoveringPassword ? "Stop attempts" : "Try candidate passwords"}
            </button>
          </div>
          {passwordProgress && recoveringPassword && (
            <div className="password-progress">
              <span
                style={{
                  width: `${Math.round(
                    (passwordProgress.attempt /
                      Math.max(1, passwordProgress.total)) *
                      100,
                  )}%`,
                }}
              />
              <small>
                Attempt {passwordProgress.attempt} of {passwordProgress.total}
              </small>
            </div>
          )}
          {passwordResult && (
            <div
              className={`password-result ${
                passwordResult.found ? "is-found" : ""
              }`}
            >
              {passwordResult.found ? <Check size={14} /> : <Info size={14} />}
              <span>
                {passwordResult.found
                  ? `Password recovered after ${passwordResult.attempts} attempts:`
                  : passwordResult.stopped === "cancelled"
                    ? `Stopped after ${passwordResult.attempts} attempts.`
                    : `No match in ${passwordResult.attempts} bounded attempts.`}
              </span>
              {passwordResult.password && <code>{passwordResult.password}</code>}
            </div>
          )}
        </section>
      )}

      {inspection.warnings.length > 0 && (
        <div className="container-warnings">
          {inspection.warnings.map((warning) => (
            <span key={warning}>
              <Info size={13} />
              {warning}
            </span>
          ))}
        </div>
      )}

      {!inspection.supported ? (
        <div className="entry-tool-empty">
          <Archive size={28} />
          <strong>Codec recognized, extraction unavailable</strong>
          <span>{inspection.warnings[0]}</span>
        </div>
      ) : (
        <div className="container-split">
          <div className="container-entry-list">
            {entries.slice(0, 4_000).map((entry) => (
              <button
                type="button"
                key={entry.path}
                className={selected?.path === entry.path ? "is-selected" : ""}
                disabled={entry.directory}
                onClick={() => void openEntry(entry)}
                title={entry.path}
              >
                <EntryGlyph kind={entry.kind} directory={entry.directory} />
                <span>{entry.path}</span>
                {entry.encrypted && <LockKeyhole size={12} />}
                <small>{entry.directory ? "folder" : formatBytes(entry.size)}</small>
              </button>
            ))}
          </div>
          <div className="container-entry-preview">
            {extracting ? (
              <div className="entry-loading">
                <LoaderCircle size={18} />
                Extracting selected entry…
              </div>
            ) : extractError ? (
              <div className="entry-tool-error compact">
                <AlertTriangle size={16} />
                <span>{extractError}</span>
              </div>
            ) : selected && extracted && extractedFormat ? (
              <>
                <div className="contained-file-head">
                  <div>
                    <span>{extractedFormat.label}</span>
                    <strong>{selected.path}</strong>
                  </div>
                  <button
                    type="button"
                    className="secondary-button compact"
                    onClick={downloadExtracted}
                  >
                    <Download size={13} />
                    Download unpacked
                  </button>
                </div>
                <FormatOverview
                  format={extractedFormat}
                  bytes={extracted}
                  stringCount={extractedStrings.length}
                />
                {extractedFormat.preview === "text" ? (
                  <pre className="contained-text-preview">
                    <code>
                      {new TextDecoder("utf-8", { fatal: false })
                        .decode(extracted)
                        .slice(0, 200_000)}
                    </code>
                  </pre>
                ) : (
                  <pre className="contained-text-preview">
                    <code>{hexDump(extracted, 4096)}</code>
                  </pre>
                )}
              </>
            ) : (
              <div className="entry-tool-empty">
                <Archive size={28} />
                <strong>Select a contained file</strong>
                <span>Extraction stays in memory and never executes the result.</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


export function RecoveryWorkbench({
  bytes,
  path,
  report,
  onToast,
}: {
  bytes: Uint8Array;
  path: string;
  report: ScanReport;
  onToast: (message: string) => void;
}) {
  const [manualKeys, setManualKeys] = useState("");
  const [result, setResult] = useState<RecoveryResult | null>(null);
  const [selectedId, setSelectedId] = useState("");
  const [running, setRunning] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const run = async () => {
    setRunning(true);
    setResult(null);
    setSelectedId("");
    setError("");
    try {
      const contextual = archiveStringKeys(
        report.strings.map((item) => item.value),
      );
      const recovered = await discoverRecoveryCandidates(bytes, path, [
        ...parseManualKeyList(manualKeys),
        ...contextual,
      ]);
      setResult(recovered);
      setSelectedId(recovered.candidates[0]?.id ?? "");
    } catch (recoveryError) {
      setError(
        recoveryError instanceof Error
          ? recoveryError.message
          : "The bounded recovery pass failed.",
      );
    } finally {
      setRunning(false);
    }
  };

  const selected =
    result?.candidates.find((candidate) => candidate.id === selectedId) ??
    result?.candidates[0] ??
    null;

  const downloadCandidate = async (candidate: RecoveryCandidate) => {
    setDownloading(true);
    setError("");
    try {
      const output = await applyRecoveryRecipe(bytes, candidate.recipe);
      const format = detectFileFormat(output, "recovered.bin");
      downloadBlob(
        new Uint8Array(output),
        suggestedRecoveredName(path, format, "deobfuscated"),
        format.mime,
      );
      onToast("Deobfuscated file downloaded");
    } catch (downloadError) {
      setError(
        downloadError instanceof Error
          ? downloadError.message
          : "JarLab could not materialize that recovery recipe.",
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="recovery-workbench">
      <div className="recovery-intro">
        <div className="recovery-emblem">
          <Sparkles size={21} />
        </div>
        <div>
          <span>BOUNDED DEOBFUSCATION LAB</span>
          <strong>Recover structure without running the file</strong>
          <p>
            Tests Base64, hex, GZIP, prepended wrappers, bit transforms, all 256
            one-byte XOR keys, byte shifts, repeating XOR, RC4, known headers,
            analyst keys, and likely keys recovered elsewhere in this sample.
          </p>
        </div>
      </div>
      <div className="recovery-controls">
        <label>
          <span>Candidate keys, one per line</span>
          <textarea
            value={manualKeys}
            onChange={(event) => setManualKeys(event.target.value)}
            placeholder={"plain-text-key\nhex:0011aaff\nbase64:SmFyTGFi"}
            rows={4}
          />
          <small>
            Prefix binary keys with <code>hex:</code> or <code>base64:</code>.
            Modern encryption is not brute-forceable here.
          </small>
        </label>
        <button
          type="button"
          className="primary-button"
          disabled={running}
          onClick={() => void run()}
        >
          {running ? <LoaderCircle size={15} /> : <Sparkles size={15} />}
          {running ? "Testing bounded transforms…" : "Find keys & rebuild"}
        </button>
      </div>
      {error && (
        <div className="entry-tool-error compact">
          <AlertTriangle size={16} />
          <span>{error}</span>
        </div>
      )}
      {result && (
        <>
          <div className="recovery-stats">
            <div>
              <span>INPUT</span>
              <strong>{result.inputFormat.label}</strong>
            </div>
            <div>
              <span>ENTROPY</span>
              <strong>{result.inputEntropy.toFixed(2)}</strong>
            </div>
            <div>
              <span>TRANSFORMS</span>
              <strong>{result.attemptedTransforms.toLocaleString()}</strong>
            </div>
            <div>
              <span>KEYS</span>
              <strong>{result.attemptedKeys.toLocaleString()}</strong>
            </div>
            <div>
              <span>CANDIDATES</span>
              <strong>{result.candidates.length}</strong>
            </div>
          </div>
          {result.candidates.length ? (
            <div className="recovery-results">
              <div className="recovery-candidate-list">
                {result.candidates.map((candidate) => (
                  <button
                    type="button"
                    key={candidate.id}
                    className={selected?.id === candidate.id ? "is-selected" : ""}
                    onClick={() => setSelectedId(candidate.id)}
                  >
                    <span className={`confidence-dot ${candidate.confidence}`} />
                    <div>
                      <strong>{candidate.recipe.label}</strong>
                      <span>
                        {candidate.format.label} · {candidate.printablePercent}%
                        printable
                      </span>
                    </div>
                    <b>{candidate.score}</b>
                  </button>
                ))}
              </div>
              {selected && (
                <div className="recovery-preview">
                  <div className="recovery-preview-head">
                    <div>
                      <span>{selected.format.label}</span>
                      <strong>{selected.recipe.label}</strong>
                    </div>
                    <button
                      type="button"
                      className="primary-button compact"
                      disabled={downloading}
                      onClick={() => void downloadCandidate(selected)}
                    >
                      {downloading ? (
                        <LoaderCircle size={13} />
                      ) : (
                        <Download size={13} />
                      )}
                      Download deobfuscated
                    </button>
                  </div>
                  {selected.recipe.key && (
                    <div className="recovery-key">
                      <span>Recovered / tested key</span>
                      <code>{selected.recipe.key}</code>
                      <small>{selected.recipe.keySource}</small>
                    </div>
                  )}
                  <div className="recovery-evidence">
                    {selected.evidence.map((item) => (
                      <span key={item}>
                        <Check size={12} />
                        {item}
                      </span>
                    ))}
                  </div>
                  <pre>
                    <code>
                      {selected.format.preview === "text"
                        ? new TextDecoder("utf-8", { fatal: false })
                            .decode(selected.preview)
                            .slice(0, 120_000)
                        : hexDump(selected.preview, 4096)}
                    </code>
                  </pre>
                </div>
              )}
            </div>
          ) : (
            <div className="entry-tool-empty">
              <Info size={26} />
              <strong>No evidence-backed transform found</strong>
              <span>
                The file may already be plain, use unsupported compression, or
                require a key derived at runtime. Add candidate keys from the
                decompiled code and try again.
              </span>
            </div>
          )}
          <div className="recovery-limitations">
            {result.limitations.map((item) => (
              <span key={item}>
                <Info size={12} />
                {item}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
